/**
 * Pointer interactions on the timeline: move, trim, ripple, roll, slip, razor and marquee.
 *
 * Drags are expressed as absolute targets computed from the state at gesture start and applied
 * with `gesture.replace`, so each pointer move re-applies the edit from a clean base and the whole
 * drag becomes one undo step.
 */
import {
  clipEnd,
  findClip,
  moveClips,
  rollEdit,
  slipClip,
  splitClip,
  trimClip,
  type Clip,
  type Id,
  type Project,
  type Sequence,
  type Track,
} from '@ve/core';
import { gesture, useEditor } from '../../state/editor-store.ts';
import { transport } from '../../state/playback.ts';

export const EDGE_HIT_PX = 7;
export const SNAP_PX = 8;

export type ClipZone = 'start' | 'end' | 'body';

export function zoneAt(offsetX: number, widthPx: number): ClipZone {
  const edge = Math.min(EDGE_HIT_PX, widthPx / 4);
  if (offsetX <= edge) return 'start';
  if (offsetX >= widthPx - edge) return 'end';
  return 'body';
}

/** Collects snap targets: clip edges (excluding ignored clips), markers, playhead and 0. */
export function snapTargets(seq: Sequence, ignore: Set<Id>): number[] {
  const set = new Set<number>([0, transport.frame]);
  for (const t of [...seq.videoTracks, ...seq.audioTracks]) {
    for (const c of t.clips) {
      if (ignore.has(c.id)) continue;
      set.add(c.start);
      set.add(clipEnd(c));
    }
  }
  for (const m of seq.markers) set.add(m.frame);
  return [...set];
}

/**
 * Returns the delta (frames) that snaps any of `edges` (+ proposed delta) to the nearest target
 * within the pixel threshold, or the proposed delta unchanged.
 */
export function snapDelta(
  edges: number[],
  delta: number,
  targets: number[],
  pxPerFrame: number,
): {
  delta: number;
  snappedTo: number | null;
} {
  const threshold = SNAP_PX / pxPerFrame;
  let best: { delta: number; dist: number; at: number } | null = null;
  for (const e of edges) {
    for (const t of targets) {
      const dist = Math.abs(e + delta - t);
      if (dist <= threshold && (!best || dist < best.dist)) best = { delta: t - e, dist, at: t };
    }
  }
  return best ? { delta: best.delta, snappedTo: best.at } : { delta, snappedTo: null };
}

/** Track id under a viewport point (rows carry `data-track-id`). */
export function trackIdAtPoint(x: number, y: number): Id | null {
  for (const el of document.elementsFromPoint(x, y)) {
    const id = (el as HTMLElement).dataset?.trackId;
    if (id) return id;
  }
  return null;
}

interface DragContext {
  project: Project;
  sequence: Sequence;
  pxPerFrame: number;
  snapping: boolean;
  onSnap(frame: number | null): void;
}

function onPointerDrag(
  e: PointerEvent | React.PointerEvent,
  handlers: { move(ev: PointerEvent, dx: number): void; end(ev: PointerEvent, moved: boolean): void },
  threshold = 3,
): void {
  const startX = e.clientX;
  const startY = e.clientY;
  let moved = false;
  const move = (ev: PointerEvent) => {
    if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < threshold) return;
    moved = true;
    handlers.move(ev, ev.clientX - startX);
  };
  const up = (ev: PointerEvent) => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
    handlers.end(ev, moved);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
}

function trackList(seq: Sequence, kind: Track['kind']): Track[] {
  return kind === 'video' ? seq.videoTracks : seq.audioTracks;
}

/** Drag the selected clips (moving across tracks of the same kind keeps relative track offsets). */
export function beginMove(e: React.PointerEvent, clip: Clip, ctx: DragContext): void {
  const selection = useEditor.getState().selection.clipIds;
  const ids = selection.includes(clip.id) ? selection : [clip.id];
  const origin = ids.flatMap((id) => {
    const loc = findClip(ctx.project, id);
    return loc ? [{ id, start: loc.clip.start, end: clipEnd(loc.clip), track: loc.track }] : [];
  });
  const anchorTrack = findClip(ctx.project, clip.id)!.track;
  const targets = snapTargets(ctx.sequence, new Set(ids));
  const edges = origin.flatMap((o) => [o.start, o.end]);
  const minStart = Math.min(...origin.map((o) => o.start));
  let begun = false;

  onPointerDrag(e, {
    move(ev, dx) {
      if (!begun) {
        gesture.begin(ids.length > 1 ? 'Move clips' : 'Move clip');
        begun = true;
      }
      let delta = Math.round(dx / ctx.pxPerFrame);
      delta = Math.max(delta, -minStart);
      if (ctx.snapping && !ev.shiftKey) {
        const s = snapDelta(edges, delta, targets, ctx.pxPerFrame);
        delta = Math.max(s.delta, -minStart);
        ctx.onSnap(s.snappedTo);
      } else ctx.onSnap(null);

      // Vertical: shift tracks by the anchor's index delta, within each kind's list.
      const overId = trackIdAtPoint(ev.clientX, ev.clientY);
      const anchorList = trackList(ctx.sequence, anchorTrack.kind);
      const overIndex = overId ? anchorList.findIndex((t) => t.id === overId) : -1;
      const trackDelta = overIndex === -1 ? 0 : overIndex - anchorList.indexOf(anchorTrack);
      const moves = origin.map((o) => {
        const list = trackList(ctx.sequence, o.track.kind);
        const idx = list.indexOf(o.track);
        const shifted = o.track.kind === anchorTrack.kind ? idx + trackDelta : idx;
        const target = list[Math.max(0, Math.min(list.length - 1, shifted))]!;
        return { clipId: o.id, trackId: target.id, start: o.start + delta };
      });
      gesture.replace(ids.length > 1 ? 'Move clips' : 'Move clip', (p) => moveClips(p, moves));
    },
    end() {
      ctx.onSnap(null);
      if (begun) gesture.commit();
    },
  });
}

/** Trim one edge. `mode` selects plain trim, ripple trim or rolling edit. */
export function beginTrim(
  e: React.PointerEvent,
  clip: Clip,
  edge: 'start' | 'end',
  mode: 'trim' | 'ripple' | 'roll',
  ctx: DragContext,
): void {
  const loc = findClip(ctx.project, clip.id);
  if (!loc) return;
  const initial = edge === 'start' ? clip.start : clipEnd(clip);
  const targets = snapTargets(ctx.sequence, new Set([clip.id]));
  // A rolling edit on a start edge rolls the cut with the previous clip.
  const rollLeft = mode === 'roll' ? (edge === 'end' ? clip : loc.track.clips[loc.index - 1]) : undefined;
  if (mode === 'roll' && (!rollLeft || clipEnd(rollLeft) !== (edge === 'end' ? clipEnd(clip) : clip.start))) {
    mode = 'trim';
  }
  const label = mode === 'roll' ? 'Roll edit' : mode === 'ripple' ? 'Ripple trim' : 'Trim';
  let begun = false;
  onPointerDrag(
    e,
    {
      move(ev, dx) {
        if (!begun) {
          gesture.begin(label);
          begun = true;
        }
        let delta = Math.round(dx / ctx.pxPerFrame);
        if (ctx.snapping && !ev.shiftKey) {
          const s = snapDelta([initial], delta, targets, ctx.pxPerFrame);
          delta = s.delta;
          ctx.onSnap(s.snappedTo);
        }
        const frame = initial + delta;
        gesture.replace(label, (p) => {
          if (mode === 'roll') rollEdit(p, rollLeft!.id, frame);
          else trimClip(p, clip.id, edge, frame, { ripple: mode === 'ripple' });
        });
        // Show the frame at the edited edge in the viewer.
        transport.seek(edge === 'start' ? frame : frame - 1);
      },
      end() {
        ctx.onSnap(null);
        if (begun) gesture.commit();
      },
    },
    1,
  );
}

export function beginSlip(e: React.PointerEvent, clip: Clip, ctx: DragContext): void {
  let begun = false;
  onPointerDrag(e, {
    move(_ev, dx) {
      if (!begun) {
        gesture.begin('Slip');
        begun = true;
      }
      // Dragging right reveals earlier source material, like most NLEs.
      const delta = -Math.round(dx / ctx.pxPerFrame);
      gesture.replace('Slip', (p) => slipClip(p, clip.id, delta));
    },
    end() {
      if (begun) gesture.commit();
    },
  });
}

export function razorAt(clip: Clip, frame: number, ctx: Pick<DragContext, 'snapping' | 'pxPerFrame'>): void {
  let f = frame;
  // Snap the cut to the playhead when close.
  if (ctx.snapping && Math.abs(transport.frame - f) * ctx.pxPerFrame <= SNAP_PX) f = transport.frame;
  if (f <= clip.start || f >= clipEnd(clip)) return;
  const doc = useEditor.getState().doc;
  doc?.apply('Cut', (p) => {
    splitClip(p, clip.id, f);
  });
}

/** Rubber-band selection in the lanes area. */
export function beginMarquee(
  e: React.PointerEvent,
  container: HTMLElement,
  onRect: (rect: { x0: number; y0: number; x1: number; y1: number } | null) => void,
  pick: (rect: DOMRect) => Id[],
  additive: boolean,
): void {
  const bounds = container.getBoundingClientRect();
  const x0 = e.clientX - bounds.left + container.scrollLeft;
  const y0 = e.clientY - bounds.top + container.scrollTop;
  const baseSelection = additive ? useEditor.getState().selection.clipIds : [];
  onPointerDrag(e, {
    move(ev) {
      const x1 = ev.clientX - bounds.left + container.scrollLeft;
      const y1 = ev.clientY - bounds.top + container.scrollTop;
      onRect({ x0, y0, x1, y1 });
      const vx0 = Math.min(x0, x1) - container.scrollLeft + bounds.left;
      const vy0 = Math.min(y0, y1) - container.scrollTop + bounds.top;
      const rect = new DOMRect(vx0, vy0, Math.abs(x1 - x0), Math.abs(y1 - y0));
      const hit = pick(rect);
      useEditor.getState().select({ clipIds: [...new Set([...baseSelection, ...hit])] });
    },
    end(_ev, moved) {
      onRect(null);
      if (!moved && !additive) useEditor.getState().clearSelection();
    },
  });
}
